import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Stepper, Step, StepButton, useMediaQuery, Fab } from "@material-ui/core";
import { getLocalizedString } from "@icarius-localization/strings";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { SaveIcon } from "@icarius-icons/index";
import {
    getCounties,
    getRegions,
    getProvinces,
    getEditableFields,
    getSectionsToHide,
    getFieldsToHide,
} from "@icarius-pages/myPersonalData/selectors";
import useHandleForm from "./useHandleForm";
import ChangePinDialog from "@icarius-common/changePinDialog";
import { myPersonalDataFields } from "@icarius-pages/myPersonalData/constants";
import { TABS, MD_DEVICE_WIDTH_BREAKPOINT } from "@icarius-utils/constants";
import useHandleStepper from "@icarius-utils/hooks/useHandleStepper";
import { getAppColor, getGeographicalDivisions } from "@icarius-app/selectors";
import Section from "./section";
import UserFormHeader from "./userFormHeader";
import DataProtectionDialog from "./dataProtectionDialog";
import CorrectionDialog from "./correctionDialog";

const SECTIONS_LIST = [
    {
        tab: TABS.personal,
        name: getLocalizedString("personales"),
    },
    {
        tab: TABS.location,
        name: getLocalizedString("locationAndOrganizationalData"),
    },
    {
        tab: TABS.contract,
        name: getLocalizedString("contrato"),
    },
    {
        tab: TABS.remuneration,
        name: getLocalizedString("compensation"),
    },
    {
        tab: TABS.time,
        name: getLocalizedString("tiempos"),
    },
    {
        name: getLocalizedString("otherDays"),
        tab: TABS.others,
    },
];

const UserForm = (props) => {
    const { data, handleUpdateEmployee } = props;

    const [changePinDialogIsOpen, setChangePinDialogIsOpen] = useState(false);
    const [dataProtectionDialogIsOpen, setDataProtectionDialogIsOpen] = useState(false);
    const [correctionDialogIsOpen, setCorrectionDialogIsOpen] = useState(false);

    const dispatch = useDispatch();
    const geographicalDivisions = useSelector(getGeographicalDivisions);
    const sectionsToHide = useSelector(getSectionsToHide);
    const fieldsToHide = useSelector(getFieldsToHide);
    const editableFields = useSelector(getEditableFields);
    const counties = useSelector(getCounties);
    const provinces = useSelector(getProvinces);
    const regions = useSelector(getRegions);
    const color = useSelector(getAppColor);

    const myPersonalDataFieldsToUse = useMemo(() => {
        let auxMyPersonalDataFields = [...myPersonalDataFields];

        auxMyPersonalDataFields = auxMyPersonalDataFields.map((item) => ({
            ...item,
            label: getLocalizedString(item.label),
        }));

        const firstLevelField = auxMyPersonalDataFields.find((item) => item.name === "REGION");
        firstLevelField.label = geographicalDivisions.firstLevel;

        const secondLevelField = auxMyPersonalDataFields.find((item) => item.name === "PROVINCIA");
        secondLevelField.label = geographicalDivisions.secondLevel;

        const thirdLevelField = auxMyPersonalDataFields.find((item) => item.name === "COMUNA");
        thirdLevelField.label = geographicalDivisions.thirdLevel;

        return auxMyPersonalDataFields;
    }, [geographicalDivisions]);

    const editableFieldsToUse = useMemo(() => {
        if (editableFields) {
            // hacer a los que NO son user fields editables
            const auxEditableFields = editableFields.map((item) => {
                return {
                    ...item,
                    editable: item.is_user_field ? item.editable : true,
                };
            });

            // asociar las regiones, comunas y provincias (que se traen de fetchs dinamicos) con el editableFields
            let countyField = auxEditableFields.find((item) => item.name === "COMUNA");
            let provincesField = auxEditableFields.find((item) => item.name === "PROVINCIA");
            let regionsField = auxEditableFields.find((item) => item.name === "REGION");

            if (countyField) countyField.options = counties;
            if (provincesField) provincesField.options = provinces;
            if (regionsField) regionsField.options = regions;

            return auxEditableFields;
        }

        return null;
    }, [editableFields, counties, provinces, regions]);

    const modify = (dataToSend) => {
        handleUpdateEmployee(dataToSend);
    };

    const openValidationError = (message) => {
        dispatch(
            openSnackbarAction({
                msg: message || getLocalizedString("invalidData"),
                severity: "error",
                duration: 10000,
            })
        );
    };

    const { formData, setFormValue, submit, fieldsWithError } = useHandleForm(
        data,
        modify,
        openValidationError,
        editableFieldsToUse
    );

    const isVerticalStepper = useMediaQuery(`(max-width:${MD_DEVICE_WIDTH_BREAKPOINT - 1}px)`);

    const steps = [
        ...SECTIONS_LIST.map((item) => ({
            name: getLocalizedString(item.name),
            tab: item.tab,
            render: () => (
                <Section
                    tab={item.tab}
                    name={getLocalizedString(item.name)}
                    formData={formData}
                    setFormValue={setFormValue}
                    fieldsToUse={myPersonalDataFieldsToUse}
                    editableFields={editableFieldsToUse}
                    fieldsToHide={fieldsToHide}
                    fieldsWithError={fieldsWithError}
                />
            ),
        })),
    ].filter((item) => {
        if (item.tab === TABS.others && !editableFieldsToUse?.some((editableField) => editableField.is_user_field))
            return false;
        if (item.tab === TABS.contract && sectionsToHide.includes("contract")) return false;
        if (item.tab === TABS.remuneration && sectionsToHide.includes("remuneration")) return false;
        if (item.tab === TABS.time && sectionsToHide.includes("time")) return false;

        return true;
    });

    const { currentStep, setCurrentStep, renderStep } = useHandleStepper(steps);

    const handleSubmit = () => {
        submit();
        setDataProtectionDialogIsOpen(false);
    };

    const handleSetNonLinearStep = (index) => {
        setCurrentStep(index);
    };

    return (
        <>
            <Grid container item xs={12} direction="row" style={{ margin: -8 }}>
                <UserFormHeader
                    data={data}
                    color={color}
                    isVerticalStepper={isVerticalStepper}
                    onOpenDataProtection={() => setDataProtectionDialogIsOpen(true)}
                    onOpenCorrection={() => setCorrectionDialogIsOpen(true)}
                    onOpenChangePin={() => setChangePinDialogIsOpen(true)}
                />

                <Fab
                    color="primary"
                    variant="extended"
                    onClick={handleSubmit}
                    style={{ position: "fixed", bottom: 30, right: 30, zIndex: 1000 }}
                >
                    <SaveIcon style={{ marginRight: 8 }} />
                    {getLocalizedString("save")}
                </Fab>
                <Grid container item xs={12} justify="center" id="stepperHeader" style={{ paddingTop: 30 }}>
                    <Stepper
                        activeStep={currentStep}
                        nonLinear={true}
                        orientation={isVerticalStepper ? "vertical" : "horizontal"}
                        alternativeLabel={!isVerticalStepper}
                        style={{ width: "100%" }}
                    >
                        {steps.map((step, index) => {
                            return (
                                <Step key={index}>
                                    <StepButton onClick={() => handleSetNonLinearStep(index)}>{step.name}</StepButton>
                                </Step>
                            );
                        })}
                    </Stepper>
                </Grid>
                <Grid container item xs={12}>
                    {renderStep()}
                </Grid>
            </Grid>
            {changePinDialogIsOpen && (
                <ChangePinDialog open={changePinDialogIsOpen} onClose={() => setChangePinDialogIsOpen(false)} />
            )}
            {dataProtectionDialogIsOpen && (
                <DataProtectionDialog
                    isOpen={dataProtectionDialogIsOpen}
                    onClose={() => setDataProtectionDialogIsOpen(false)}
                    formData={formData}
                    setFormValue={setFormValue}
                    handleSubmit={handleSubmit}
                />
            )}
            {correctionDialogIsOpen && (
                <CorrectionDialog isOpen={correctionDialogIsOpen} onClose={() => setCorrectionDialogIsOpen(false)} />
            )}
        </>
    );
};

export default UserForm;
